/* eslint-disable react/jsx-props-no-spreading */
import { useEffect, useState, useMemo } from 'react';
import {
  Box,
  styled,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  CircularProgress,
  SelectChangeEvent,
} from '@mui/material';
import { PictureAsPdf, TableChart, Summarize } from '@mui/icons-material';
import { AppBarWithApiRef } from '@Components/app-bar';
import { paymentColumns } from './columns';
import Table from '@Components/table';
import useTableRequestParams from 'hooks/useTableRequestParams';
import Papa from 'papaparse';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { usePaymentsByCustomerQuery } from '@Store/api/paymentsSlice';
import { useGetCustomersQuery } from '@Store/api/customersSlice';
import { exportData } from '@Utils/export-csv';
import { handleFetchError } from '@Utils/handleError';
import { ICustomer } from '@Types/customerType';
import { endOfDay, startOfDay, startOfMonth, startOfWeek, subDays } from 'date-fns';
import companyLogo from '../../assets/images/companylogo.png';

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
}

const Container = styled(Box)`
  padding: 0px;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

const FilterContainer = styled(Box)`
  padding: 16px;
  background-color: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
`;

const TIME_PERIODS = {
  TODAY: 'today',
  YESTERDAY: 'yesterday',
  THIS_WEEK: 'this_week',
  LAST_WEEK: 'last_week',
  THIS_MONTH: 'this_month',
  LAST_MONTH: 'last_month',
  CUSTOM: 'custom',
} as const;

type TimePeriod = keyof typeof TIME_PERIODS;

declare module 'jspdf' {
  interface jsPDF {
    lastAutoTable: { finalY: number };
  }
}

const formatCurrency = (amount: number | undefined): string => {
  if (amount === undefined || amount === null) return '£0.00';

  return `£${amount.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}`;
};

const formatNumber = (num: number | undefined): string => {
  if (num === undefined || num === null) return '0';

  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export function PaymentHistoryByCustomer() {
  const tableParams = useTableRequestParams();
  // Filter controls edit the "draft" values only; nothing is sent to the server until the user
  // presses Go, which copies the draft into appliedFilters (the values the report is loaded with).
  const [draftCustomerId, setDraftCustomerId] = useState<string>('all');
  const [draftCustomerObj, setDraftCustomerObj] = useState<{
    id: string;
    name: string;
  } | null>({ id: 'all', name: 'All Customers' });
  const [draftTimePeriod, setDraftTimePeriod] = useState<TimePeriod>('THIS_MONTH');
  const [draftStartDate, setDraftStartDate] = useState<Date | null>(new Date());
  const [draftEndDate, setDraftEndDate] = useState<Date | null>(new Date());

  const [appliedFilters, setAppliedFilters] = useState<{
    customerId: string;
    customerObj: { id: string; name: string } | null;
    timePeriod: TimePeriod;
    startDate: Date | null;
    endDate: Date | null;
  }>({
    customerId: 'all',
    customerObj: { id: 'all', name: 'All Customers' },
    timePeriod: 'THIS_MONTH',
    startDate: new Date(),
    endDate: new Date(),
  });

  // The loaded report, its column headers and its exports all describe the applied filters.
  const {
    customerId: selectedCustomerId,
    customerObj: selectedCustomerObj,
    timePeriod,
    startDate,
    endDate,
  } = appliedFilters;
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [pendingExportIds, setPendingExportIds] = useState<string[] | undefined>(undefined);
  const [customerSearchInput, setCustomerSearchInput] = useState('');
  const [customerInputValue, setCustomerInputValue] = useState('All Customers');

  const debouncedCustomerSearch = useDebounce(customerSearchInput, 300);

  const customersParams = useMemo(
    () => ({
      pageSize: 500,
      currentPage: 1,
      search: debouncedCustomerSearch,
      sortFields: tableParams.sortFields,
    }),
    [debouncedCustomerSearch, tableParams.sortFields]
  );

  const { data: customersReport, isFetching: isFetchingCustomers } = useGetCustomersQuery(customersParams);
  const allCustomers = customersReport?.items ?? [];

  const customerOptions = useMemo(() => {
    const options = [{ id: 'all', name: 'All Customers' }, ...allCustomers];

    if (
      draftCustomerId !== 'all' &&
      draftCustomerObj &&
      !options.some((opt) => opt.id === draftCustomerId)
    ) {
      options.push(draftCustomerObj);
    }

    return options;
  }, [allCustomers, draftCustomerId, draftCustomerObj]);

  const {
    data: customerPayments,
    error: tableDataError,
    isFetching: isFetchingPayments,
    refetch: refetchPayments,
  } = usePaymentsByCustomerQuery({
    ...tableParams,
    customerId: selectedCustomerId,
    pageSize: tableParams.pageSize,
    currentPage: tableParams.currentPage,
    sortFields: tableParams.sortFields,
    search: tableParams.search,
    startDate: timePeriod !== 'CUSTOM' ? getDateRange(timePeriod).startDate : startDate?.toISOString(),
    endDate: timePeriod !== 'CUSTOM' ? getDateRange(timePeriod).endDate : endDate?.toISOString(),
  });

  const payments = customerPayments;

  function getDateRange(period: TimePeriod) {
    const now = new Date();

    switch (period) {
      case 'TODAY':
        return { startDate: startOfDay(now).toISOString(), endDate: endOfDay(now).toISOString() };
      
        case 'YESTERDAY': {
        const yesterday = subDays(now, 1);

        return { startDate: startOfDay(yesterday).toISOString(), endDate: endOfDay(yesterday).toISOString() };
      }
      case 'THIS_WEEK':
        return { startDate: startOfWeek(now).toISOString(), endDate: endOfDay(now).toISOString() };
      
        case 'LAST_WEEK': {
        const lastWeekStart = startOfWeek(subDays(now, 7));
        const lastWeekEnd = endOfDay(new Date(lastWeekStart.getTime() + 6 * 24 * 60 * 60 * 1000));

        return { startDate: lastWeekStart.toISOString(), endDate: lastWeekEnd.toISOString() };
      }
      case 'THIS_MONTH':
        return { startDate: startOfMonth(now).toISOString(), endDate: endOfDay(now).toISOString() };
      
      case 'LAST_MONTH': {
        const lastMonthStart = startOfMonth(new Date(now.getFullYear(), now.getMonth() - 1, 1));
        const lastMonthEnd = endOfDay(new Date(now.getFullYear(), now.getMonth(), 0));

        return { startDate: lastMonthStart.toISOString(), endDate: lastMonthEnd.toISOString() };
      }
      case 'CUSTOM':
        return {
          startDate: startDate ? startDate.toISOString() : '',
          endDate: endDate ? endDate.toISOString() : '',
        };
      default:
        return { startDate: startOfMonth(now).toISOString(), endDate: endOfDay(now).toISOString() };
    }
  }

  // These only change the draft filters - the report is not requested until Go is pressed.
  const handleCustomerChange = (customerId: string, customerName?: string) => {
    setDraftCustomerId(customerId);

    if (customerId === 'all') {
      setDraftCustomerObj({ id: 'all', name: 'All Customers' });
      setCustomerInputValue('All Customers');
    } else if (customerName) {
      setDraftCustomerObj({ id: customerId, name: customerName });
      setCustomerInputValue(customerName);
    }

    setCustomerSearchInput('');
  };

  const handleTimePeriodChange = (event: SelectChangeEvent<TimePeriod>) => {
    const newTimePeriod = event.target.value as TimePeriod;

    setDraftTimePeriod(newTimePeriod);

    if (newTimePeriod === 'CUSTOM' && (!draftStartDate || !draftEndDate)) {
      setDraftStartDate(new Date());
      setDraftEndDate(new Date());
    }
  };

  const isDraftCustomRangeInvalid =
    draftTimePeriod === 'CUSTOM' &&
    (!draftStartDate ||
      !draftEndDate ||
      Number.isNaN(draftStartDate.getTime()) ||
      Number.isNaN(draftEndDate.getTime()) ||
      draftStartDate.getTime() > draftEndDate.getTime());

  const handleGo = () => {
    if (isDraftCustomRangeInvalid) return;

    const unchanged =
      draftCustomerId === appliedFilters.customerId &&
      draftTimePeriod === appliedFilters.timePeriod &&
      (draftTimePeriod !== 'CUSTOM' ||
        (draftStartDate?.getTime() === appliedFilters.startDate?.getTime() &&
          draftEndDate?.getTime() === appliedFilters.endDate?.getTime()));

    if (tableParams.setCurrentPage) tableParams.setCurrentPage(1);

    setAppliedFilters({
      customerId: draftCustomerId,
      customerObj: draftCustomerObj,
      timePeriod: draftTimePeriod,
      startDate: draftStartDate,
      endDate: draftEndDate,
    });

    // Same filters as already loaded: nothing changes for the query, so ask for fresh data explicitly.
    if (unchanged) refetchPayments();
  };

  const columnsWithCustomerName = useMemo(
    () =>
      paymentColumns.map((col) => {
        const newCol = { ...col };

        if (
          col.field === 'convenienceFee' ||
          col.field === 'reminderFee' ||
          col.field === 'sessionPrice' ||
          col.field === 'tax' ||
          col.field === 'sessionTotal'
        ) {
          newCol.renderCell = (params: any) => {
            const value = params.value;
            const formattedValue = typeof value === 'number' ? `£${value.toFixed(2)}` : '£0.00';

            return <span>{formattedValue}</span>;
          };
        } else {
          newCol.renderCell = (params: any) => <span>{params.value}</span>;
        }

        if (col.field === 'subareaName') {
          return {
            ...newCol,
            headerName: selectedCustomerId !== 'all' ? 'Subarea (Customer)' : 'Subarea Name',
            valueGetter: (params: any) => {
              const subArea = params.row.subAreaName || '';

              if (selectedCustomerId !== 'all' && selectedCustomerObj?.name) {
                return `${subArea} (${selectedCustomerObj.name})`;
              }

              return subArea;
            },
            renderCell: (params: any) => (
              <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{params.value}</span>
            ),
          };
        }

        return newCol;
      }),
    [paymentColumns, selectedCustomerId, selectedCustomerObj]
  );

  // ------------------------------------------------------------------------
  // EXPORT FUNCTIONS (Session Statistics excluded, Total Sessions Amount removed)
  // ------------------------------------------------------------------------
  const performSummaryExport = async (format: 'csv' | 'pdf' = 'pdf') => {
    const selectedCustomer =
      selectedCustomerId === 'all'
        ? 'All Customers'
        : allCustomers.find((c: ICustomer) => c.id === selectedCustomerId)?.name || 'Unknown Customer';

    const formatTimePeriod = (period: TimePeriod) =>
      period
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (l) => l.toUpperCase());

    const dateRangeInfo =
      timePeriod === 'CUSTOM'
        ? `Custom Range: ${startDate?.toLocaleDateString()} - ${endDate?.toLocaleDateString()}`
        : (() => {
            const dateRange = getDateRange(timePeriod);
            const start = new Date(dateRange.startDate);
            const end = new Date(dateRange.endDate);

            return `${formatTimePeriod(timePeriod)}: ${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
          })();

    const firstItem = payments?.items?.[0];

    // Removed "Total Sessions Amount" row
    const summaryData = [
      ['Total Captured Transactions:', formatNumber(firstItem?.totalTransactions)],
      ['App Transactions:', formatNumber(firstItem?.totalAppPayment)],
      ['Web Transactions:', formatNumber(firstItem?.totalWebPayment)],
      ['IOS mini Transactions:', formatNumber(firstItem?.totalIosMiniWebPayment)],
      ['Android mini Transactions:', formatNumber(firstItem?.totalAndroidMiniWebPayment)],
      ['GPay Transactions:', formatNumber(firstItem?.totalGooglePayCount)],
      ['ApplePay Transactions:', formatNumber(firstItem?.totalApplePayCount)],
      ['Card Transactions:', formatNumber(firstItem?.totalCreditCardCount)],
      [
        'Convenience Fee:',
        formatCurrency(payments?.items?.reduce((sum, item) => sum + (item.convenienceFee ?? 0), 0) ?? 0),
      ],
      [
        'Reminder Fee:',
        formatCurrency(payments?.items?.reduce((sum, item) => sum + (item.reminderFee ?? 0), 0) ?? 0),
      ],
      [
        'Total Income Received (Captured) (ex VAT):',
        formatCurrency(payments?.items?.reduce((sum, item) => sum + (item.sessionPrice ?? 0), 0) ?? 0),
      ],
      ['VAT:', formatCurrency(payments?.items?.reduce((sum, item) => sum + (item.tax ?? 0), 0) ?? 0)],
      [
        'Total Income Received (Captured):',
        formatCurrency(payments?.items?.reduce((sum, item) => sum + (item.sessionTotal ?? 0), 0) ?? 0),
      ],
      ['Refunded Total:', formatCurrency(firstItem?.refundTotal)],
      ['Refunded Total for Previous Months:', formatCurrency(firstItem?.refundTotalForPreviousDates)],
      [
        'Total Payable:',
        formatCurrency(
          (payments?.items?.reduce((sum, item) => sum + (item.sessionAmount ?? 0), 0) ?? 0) -
            (firstItem?.refundTotal ?? 0) -
            (firstItem?.refundTotalForPreviousDates ?? 0)
        ),
      ],
    ];

    if (format === 'pdf') {
      const doc = new jsPDF({ compress: true });

      try {
        doc.addImage(companyLogo, 'PNG', 150, 10, 45, 22);
      } catch (error) {
        // ignore missing logo
      }

      doc.setFontSize(16);
      doc.text('PAYMENT SUMMARY REPORT', 14, 20);
      doc.setFontSize(10);
      doc.text(`Customer: ${selectedCustomer}`, 14, 30);
      doc.text(`Date Range: ${dateRangeInfo}`, 14, 40);
      doc.setFontSize(12);
      doc.text('SUMMARY STATISTICS', 14, 50);

      autoTable(doc, {
        startY: 55,
        head: [['Metric', 'Value']],
        body: summaryData,
        theme: 'grid',
        styles: { fontSize: 10 },
        headStyles: { fillColor: [66, 66, 66] },
      });

      const finalY = doc.lastAutoTable.finalY + 10;

      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text(`Exported on: ${new Date().toLocaleString()}`, 14, finalY);
      doc.save(`payment-summary-${new Date().toISOString().split('T')[0]}.pdf`);
    } else {
      const exportDataArray: (string | number)[][] = [
        ['PAYMENT SUMMARY REPORT'],
        [''],
        ['Customer:', selectedCustomer],
        ['Date Range:', dateRangeInfo],
        ['Exported on:', new Date().toLocaleString()],
        [''],
        ['SUMMARY STATISTICS'],
        ...summaryData.map((row) => [row[0], row[1]]),
      ];
      const csv = Papa.unparse(exportDataArray);

      exportData(csv);
    }
  };

  const performExport = async (selectedIds?: string[], format: 'csv' | 'pdf' = 'csv') => {
    const selectedCustomer =
      selectedCustomerId === 'all'
        ? 'All Customers'
        : allCustomers.find((c: ICustomer) => c.id === selectedCustomerId)?.name || 'Unknown Customer';

    const formatTimePeriod = (period: TimePeriod) =>
      period
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (l) => l.toUpperCase());

    const dateRangeInfo =
      timePeriod === 'CUSTOM'
        ? `Custom Range: ${startDate?.toLocaleDateString()} - ${endDate?.toLocaleDateString()}`
        : (() => {
            const dateRange = getDateRange(timePeriod);
            const start = new Date(dateRange.startDate);
            const end = new Date(dateRange.endDate);

            return `${formatTimePeriod(timePeriod)}: ${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
          })();

    const paymentsToExport = selectedIds
      ? (payments?.items || []).filter((payment) => selectedIds.includes(payment.id))
      : payments?.items || [];

    const isCurrencyColumn = (column: any) => {
      const currencyFields = ['amount', 'fee', 'price', 'tax', 'total', 'refund', 'convenience', 'reminder', 'session'];

      return currencyFields.some(
        (field) => column.field?.toLowerCase().includes(field) || column.headerName?.toLowerCase().includes(field)
      );
    };

    const formattedColumns = columnsWithCustomerName.map((column) => {
      if (isCurrencyColumn(column)) {
        return {
          ...column,
          valueGetter: (params: any) => {
            const originalValue = column.valueGetter ? column.valueGetter(params) : params.value;

            if (typeof originalValue === 'number' || !isNaN(parseFloat(originalValue))) {
              return formatCurrency(parseFloat(originalValue));
            }

            return originalValue;
          },
        };
      }

      return column;
    });

    const firstItem = payments?.items?.[0];

    // Removed "Total Sessions Amount" row
    const summaryRows = [
      ['Total Captured Transactions:', formatNumber(firstItem?.totalTransactions)],
      ['App Transactions:', formatNumber(firstItem?.totalAppPayment)],
      ['Web Transactions:', formatNumber(firstItem?.totalWebPayment)],
      ['IOS mini Transactions:', formatNumber(firstItem?.totalIosMiniWebPayment)],
      ['Android mini Transactions:', formatNumber(firstItem?.totalAndroidMiniWebPayment)],
      ['GPay Transactions:', formatNumber(firstItem?.totalGooglePayCount)],
      ['ApplePay Transactions:', formatNumber(firstItem?.totalApplePayCount)],
      ['Card Transactions:', formatNumber(firstItem?.totalCreditCardCount)],
      [
        'Convenience Fee:',
        formatCurrency(payments?.items?.reduce((sum, item) => sum + (item.convenienceFee ?? 0), 0) ?? 0),
      ],
      [
        'Reminder Fee:',
        formatCurrency(payments?.items?.reduce((sum, item) => sum + (item.reminderFee ?? 0), 0) ?? 0),
      ],
      [
        'Total Income Received (Captured) (ex VAT):',
        formatCurrency(payments?.items?.reduce((sum, item) => sum + (item.sessionPrice ?? 0), 0) ?? 0),
      ],
      ['VAT:', formatCurrency(payments?.items?.reduce((sum, item) => sum + (item.tax ?? 0), 0) ?? 0)],
      [
        'Total Income Received (Captured):',
        formatCurrency(payments?.items?.reduce((sum, item) => sum + (item.sessionTotal ?? 0), 0) ?? 0),
      ],
      ['Refunded Total:', formatCurrency(firstItem?.refundTotal)],
      ['Refunded Total for Previous Months:', formatCurrency(firstItem?.refundTotalForPreviousDates)],
      [
        'Total Payable:',
        formatCurrency(
          (payments?.items?.reduce((sum, item) => sum + (item.sessionAmount ?? 0), 0) ?? 0) -
            (firstItem?.refundTotal ?? 0) -
            (firstItem?.refundTotalForPreviousDates ?? 0)
        ),
      ],
    ];

    if (format === 'pdf') {
      const doc = new jsPDF({ compress: true, orientation: 'landscape' });

      try {
        doc.addImage(companyLogo, 'PNG', 250, 10, 45, 22);
      } catch (error) {
        // ignore missing logo
      }

      doc.setFontSize(16);
      doc.text('PAYMENT SUMMARY REPORT', 14, 20);
      doc.setFontSize(10);
      doc.text(`Customer: ${selectedCustomer}`, 14, 30);
      doc.text(`Date Range: ${dateRangeInfo}`, 14, 40);
      doc.setFontSize(12);
      doc.text('SUMMARY STATISTICS', 14, 50);

      autoTable(doc, {
        startY: 55,
        head: [['Metric', 'Value']],
        body: summaryRows,
        theme: 'grid',
        styles: { fontSize: 10 },
        headStyles: { fillColor: [66, 66, 66] },
      });

      const finalY = doc.lastAutoTable.finalY + 10;

      doc.setFontSize(12);
      doc.text('TRANSACTION DETAILS', 14, finalY);

      const columnWidths: Record<string, number> = {
        'Settled Date': 28,
        'Start Date': 28,
        'Exit Date': 28,
        'VRM/VRN': 25,
        'Subarea (Customer)': 50,
        'Subarea Name': 50,
        'Access Type': 22,
        'Conv Fee': 18,
        'Remi Fee': 18,
        'SessionPrice': 22,
        VAT: 18,
        'Session Total': 22,
        Status: 20,
      };
      const pdfColumns = formattedColumns.filter((col) => col.field !== 'userid' && col.headerName !== 'User ID');
      const tableColumns = pdfColumns.map((col) => ({
        header: col.headerName!,
        dataKey: col.field,
        cellWidth: columnWidths[col.headerName!] || 'auto',
      }));
      const tableData = paymentsToExport.map((payment) => {
        const row: Record<string, any> = {};

        pdfColumns.forEach((col) => {
          row[col.field] = col.valueGetter!({ row: payment } as any);
        });

        return row;
      });

      autoTable(doc, {
        startY: finalY + 5,
        columns: tableColumns,
        body: tableData,
        theme: 'grid',
        styles: { fontSize: 8, cellPadding: 1.5, overflow: 'linebreak' },
        headStyles: { fillColor: [66, 66, 66] },
        pageBreak: 'auto',
        // Highlight refunded transactions across the whole row
        didParseCell: (hookData: any) => {
          if (hookData.section === 'body' && paymentsToExport[hookData.row.index]?.status === 'REFUND') {
            hookData.cell.styles.fillColor = [253, 236, 234];
          }
        },
      });

      doc.save(`payment-report-${new Date().toISOString().split('T')[0]}.pdf`);
    } else {
      const exportDataArray: (string | number)[][] = [
        ['PAYMENT SUMMARY REPORT'],
        [''],
        ['Customer:', selectedCustomer],
        ['Date Range:', dateRangeInfo],
        [''],
        ['SUMMARY STATISTICS'],
        ...summaryRows.map((row) => [row[0], row[1]]),
        [''],
        ['TRANSACTION DETAILS'],
        [''],
        formattedColumns.map((column) => column.headerName!),
      ];

      paymentsToExport.forEach((data) => {
        const rowData = formattedColumns.map((column) => column.valueGetter!({ row: data } as any));

        exportDataArray.push(rowData as (string | number)[]);
      });

      const csv = Papa.unparse(exportDataArray);

      exportData(csv);
    }
  };

  const handleExportClick = (selectedIds?: string[]) => {
    setPendingExportIds(selectedIds);
    setExportDialogOpen(true);
  };

  const handleExportConfirm = (format: 'csv' | 'pdf', exportType: 'full' | 'summary' = 'full') => {
    setExportDialogOpen(false);

    if (exportType === 'summary') performSummaryExport(format);
    else performExport(pendingExportIds, format);
  };

  const handleExportCancel = () => {
    setExportDialogOpen(false);
    setPendingExportIds(undefined);
  };

  const handleExport = (selectedIds?: string[]) => handleExportClick(selectedIds);

  useEffect(() => {
    handleFetchError(tableDataError);
  }, [tableDataError]);

  function formatToLocalDatetime(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  const firstPayment = payments?.items?.[0];

  return (
    <Container id='payment-container'>
      <FilterContainer>
        <Autocomplete
          sx={{ minWidth: 200, maxWidth: 250 }}
          options={customerOptions}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={draftCustomerObj}
          inputValue={customerInputValue}
          onInputChange={(_event, newInputValue, reason) => {
            if (reason === 'input') {
              setCustomerInputValue(newInputValue);
              setCustomerSearchInput(newInputValue);
            } else if (reason === 'reset') {
              setCustomerInputValue(newInputValue);
            }
          }}
          onChange={(_event, newValue) => {
            if (newValue) handleCustomerChange(newValue.id, newValue.name);
            else handleCustomerChange('all', 'All Customers');
          }}
          loading={isFetchingCustomers}
          loadingText='Loading customers...'
          noOptionsText={customerSearchInput ? 'No customers found' : 'Type to search customers'}
          renderInput={(params) => (
            <TextField
              {...params}
              label='Filter by Customer'
              variant='outlined'
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isFetchingCustomers ? <CircularProgress color='inherit' size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        <FormControl sx={{ maxWidth: 200, minWidth: 150 }}>
          <InputLabel id='time-period-label'>Time Period</InputLabel>
          <Select label='Time Period' labelId='time-period-label' onChange={handleTimePeriodChange} value={draftTimePeriod}>
            <MenuItem value='TODAY'>Today</MenuItem>
            <MenuItem value='YESTERDAY'>Yesterday</MenuItem>
            <MenuItem value='THIS_WEEK'>This Week</MenuItem>
            <MenuItem value='LAST_WEEK'>Last Week</MenuItem>
            <MenuItem value='THIS_MONTH'>This Month</MenuItem>
            <MenuItem value='LAST_MONTH'>Last Month</MenuItem>
            <MenuItem value='CUSTOM'>Custom</MenuItem>
          </Select>
        </FormControl>

        {draftTimePeriod === 'CUSTOM' && (
          <>
            <TextField
              InputLabelProps={{ shrink: true }}
              label='Start Date & Time'
              onChange={(e) => setDraftStartDate(new Date(e.target.value))}
              sx={{ minWidth: 200, mr: 2 }}
              type='datetime-local'
              value={draftStartDate && !Number.isNaN(draftStartDate.getTime()) ? formatToLocalDatetime(draftStartDate) : ''}
            />
            <TextField
              InputLabelProps={{ shrink: true }}
              label='End Date & Time'
              onChange={(e) => setDraftEndDate(new Date(e.target.value))}
              sx={{ minWidth: 200 }}
              type='datetime-local'
              value={draftEndDate && !Number.isNaN(draftEndDate.getTime()) ? formatToLocalDatetime(draftEndDate) : ''}
            />
          </>
        )}

        <Button
          disabled={isFetchingPayments || isDraftCustomRangeInvalid}
          onClick={handleGo}
          startIcon={isFetchingPayments ? <CircularProgress color='inherit' size={16} /> : undefined}
          sx={{ minWidth: 90, height: 56 }}
          variant='contained'
        >
          Go
        </Button>
      </FilterContainer>

      {/* Session Statistics Card (includes Total Sessions Amount) */}
      <Box
        sx={{
          p: 2,
          mb: 2,
          bgcolor: '#e8f0fe',
          borderRadius: 1,
          border: '1px solid',
          borderColor: '#90caf9',
        }}
      >
        <Typography gutterBottom sx={{ fontWeight: 'bold' }} variant='h6'>
          Session Statistics
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={6} sm={4} md={2}>
            <Typography color='text.secondary' variant='body2'>
              Total Sessions:
            </Typography>
            <Typography fontWeight='medium' variant='body1'>
              {formatNumber(firstPayment?.totalSessions)}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Typography color='text.secondary' variant='body2'>
              Captured Sessions:
            </Typography>
            <Typography fontWeight='medium' variant='body1'>
              {formatNumber(firstPayment?.capturedSessions)}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Typography color='text.secondary' variant='body2'>
              Refunded Sessions:
            </Typography>
            <Typography fontWeight='medium' variant='body1'>
              {formatNumber(firstPayment?.refundedSessions)}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Typography color='text.secondary' variant='body2'>
              PSP Reversed Sessions:
            </Typography>
            <Typography fontWeight='medium' variant='body1'>
              {formatNumber(firstPayment?.reversedSessions)}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Typography color='text.secondary' variant='body2'>
              Preauthorized (Not Captured):
            </Typography>
            <Typography fontWeight='medium' variant='body1'>
              {formatNumber(firstPayment?.preauthorizedNotCapturedSessions)}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Typography color='text.secondary' variant='body2'>
              Courtesy/Free Sessions:
            </Typography>
            <Typography fontWeight='medium' variant='body1'>
              {formatNumber(firstPayment?.preauthorizedZeroAmountSessions)}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Typography color='text.secondary' variant='body2'>
              Current Sessions:
            </Typography>
            <Typography fontWeight='medium' variant='body1'>
              {formatNumber(firstPayment?.preauthorizedPositiveAmountSessions)}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Typography color='text.secondary' variant='body2'>
              Total Sessions Amount:
            </Typography>
            <Typography fontWeight='medium' variant='body1'>
              {formatCurrency((firstPayment?.saleAmount ?? 0) - (firstPayment?.refundTotal ?? 0))}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {/* Payment Summary Card (financial & transaction metrics only, no session amounts) */}
      <Box bgcolor='grey.100' border='1px solid' borderColor='grey.300' borderRadius={1} mb={2} p={2}>
        <Typography gutterBottom sx={{ fontWeight: 'bold' }} variant='h6'>
          Payment Summary
        </Typography>

        <Grid container spacing={2}>
          <Grid item md={2} sm={6} xs={12}>
            <Typography color='text.secondary' variant='body2'>
              Total Captured Transactions:
            </Typography>
            <Typography fontWeight='medium' variant='body1'>
              {formatNumber(firstPayment?.totalTransactions)}
            </Typography>
          </Grid>
          <Grid item md={2} sm={6} xs={12}>
            <Typography color='text.secondary' variant='body2'>
              App Transactions:
            </Typography>
            <Typography fontWeight='medium' variant='body1'>
              {formatNumber(firstPayment?.totalAppPayment)}
            </Typography>
          </Grid>
          <Grid item md={2} sm={6} xs={12}>
            <Typography color='text.secondary' variant='body2'>
              Web Transactions:
            </Typography>
            <Typography fontWeight='medium' variant='body1'>
              {formatNumber(firstPayment?.totalWebPayment)}
            </Typography>
          </Grid>
          <Grid item md={2} sm={6} xs={12}>
            <Typography color='text.secondary' variant='body2'>
              IOS mini Transactions:
            </Typography>
            <Typography fontWeight='medium' variant='body1'>
              {formatNumber(firstPayment?.totalIosMiniWebPayment)}
            </Typography>
          </Grid>
          <Grid item md={2} sm={6} xs={12}>
            <Typography color='text.secondary' variant='body2'>
              Android mini Transactions:
            </Typography>
            <Typography fontWeight='medium' variant='body1'>
              {formatNumber(firstPayment?.totalAndroidMiniWebPayment)}
            </Typography>
          </Grid>
          <Grid item md={2} sm={6} xs={12}>
            <Typography color='text.secondary' variant='body2'>
              Apple Pay Transactions:
            </Typography>
            <Typography fontWeight='medium' variant='body1'>
              {formatNumber(firstPayment?.totalApplePayCount)}
            </Typography>
          </Grid>
          <Grid item md={2} sm={6} xs={12}>
            <Typography color='text.secondary' variant='body2'>
              GPay Transactions:
            </Typography>
            <Typography fontWeight='medium' variant='body1'>
              {formatNumber(firstPayment?.totalGooglePayCount)}
            </Typography>
          </Grid>
          <Grid item md={2} sm={6} xs={12}>
            <Typography color='text.secondary' variant='body2'>
              Card Transactions:
            </Typography>
            <Typography fontWeight='medium' variant='body1'>
              {formatNumber(firstPayment?.totalCreditCardCount)}
            </Typography>
          </Grid>
          <Grid item md={2} sm={6} xs={12}>
            <Typography color='text.secondary' variant='body2'>
              Convenience Fee:
            </Typography>
            <Typography fontWeight='medium' variant='body1'>
              {formatCurrency(payments?.items?.reduce((sum, item) => sum + (item.convenienceFee ?? 0), 0) ?? 0)}
            </Typography>
          </Grid>
          <Grid item md={2} sm={6} xs={12}>
            <Typography color='text.secondary' variant='body2'>
              Reminder Fee:
            </Typography>
            <Typography fontWeight='medium' variant='body1'>
              {formatCurrency(payments?.items?.reduce((sum, item) => sum + (item.reminderFee ?? 0), 0) ?? 0)}
            </Typography>
          </Grid>
          <Grid item md={2} sm={6} xs={12}>
            <Typography color='text.secondary' variant='body2'>
              Total Income Received (captured) (ex VAT):
            </Typography>
            <Typography fontWeight='medium' variant='body1'>
              {formatCurrency(payments?.items?.reduce((sum, item) => sum + (item.sessionPrice ?? 0), 0) ?? 0)}
            </Typography>
          </Grid>
          <Grid item md={2} sm={6} xs={12}>
            <Typography color='text.secondary' variant='body2'>
              VAT:
            </Typography>
            <Typography fontWeight='medium' variant='body1'>
              {formatCurrency(payments?.items?.reduce((sum, item) => sum + (item.tax ?? 0), 0) ?? 0)}
            </Typography>
          </Grid>
          <Grid item md={2} sm={6} xs={12}>
            <Typography color='text.secondary' variant='body2'>
              Total Income Received (captured):
            </Typography>
            <Typography fontWeight='medium' variant='body1'>
              {formatCurrency(payments?.items?.reduce((sum, item) => sum + (item.sessionTotal ?? 0), 0) ?? 0)}
            </Typography>
          </Grid>
          <Grid item md={2} sm={6} xs={12}>
            <Typography color='text.secondary' variant='body2'>
              Refunded Total:
            </Typography>
            <Typography fontWeight='medium' variant='body1'>
              {formatCurrency(firstPayment?.refundTotal)}
            </Typography>
          </Grid>
          <Grid item md={2} sm={6} xs={12}>
            <Typography color='text.secondary' variant='body2'>
              Refunded Total for Previous Months:
            </Typography>
            <Typography fontWeight='medium' variant='body1'>
              {formatCurrency(firstPayment?.refundTotalForPreviousDates)}
            </Typography>
          </Grid>
          <Grid item md={2} sm={6} xs={12}>
            <Typography color='text.secondary' variant='body2'>
              Total Payable:
            </Typography>
            <Typography fontWeight='medium' variant='body1'>
              {formatCurrency(
                (payments?.items?.reduce((sum, item) => sum + (item.sessionAmount ?? 0), 0) ?? 0) -
                  (firstPayment?.refundTotal ?? 0) -
                  (firstPayment?.refundTotalForPreviousDates ?? 0)
              )}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {/* Export Dialog */}
      <Dialog fullWidth maxWidth='sm' onClose={handleExportCancel} open={exportDialogOpen}>
        <DialogTitle sx={{ pb: 1, textAlign: 'center' }}>Choose Export Format</DialogTitle>

        <DialogContent sx={{ py: 2, textAlign: 'center' }}>
          <Typography color='text.secondary' gutterBottom variant='body1'>
            {pendingExportIds && pendingExportIds.length > 0
              ? `Export ${pendingExportIds.length} selected row(s) as:`
              : 'How would you like to export the data?'}
          </Typography>
        </DialogContent>

        <DialogActions sx={{ flexWrap: 'wrap', gap: 2, justifyContent: 'center', pb: 3, px: 3 }}>
          <Button
            onClick={() => handleExportConfirm('csv', 'full')}
            startIcon={<TableChart />}
            sx={{ flex: '1 1 120px', minWidth: 140, py: 1.5 }}
            variant='outlined'
          >
            Full CSV
          </Button>
          <Button
            onClick={() => handleExportConfirm('pdf', 'full')}
            startIcon={<PictureAsPdf />}
            sx={{ flex: '1 1 120px', minWidth: 140, py: 1.5 }}
            variant='contained'
          >
            Full PDF
          </Button>
          <Button
            color='secondary'
            onClick={() => handleExportConfirm('pdf', 'summary')}
            startIcon={<Summarize />}
            sx={{ flex: '1 1 120px', minWidth: 140, py: 1.5 }}
            variant='outlined'
          >
            Summary Only
          </Button>
        </DialogActions>

        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button color='inherit' onClick={handleExportCancel}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Table
        checkboxSelection
        columns={columnsWithCustomerName}
        getRowClassName={(params) => (params.row.status === 'REFUND' ? 'row-refunded' : '')}
        handleExport={handleExport}
        printable
        rows={payments?.items || []}
        toolbar={AppBarWithApiRef}
        toolbarTitle='Payment History By Customer'
      />
    </Container>
  );
}